/**
 * useListQueryState - URL-State Hook für Listen
 * 
 * Synchronisiert Filter/Sort State mit URL Query Params.
 * Lesbare URLs: ?q=...&status=active&roles=WORKER,ADMIN&sort=name&dir=asc
 */

import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ListState, StatusFilter, SortDirection, TriStateFilter, UrlParamConfig } from './types';

const DEBOUNCE_MS = 300;

export function useListQueryState<
  TFilters extends object,
  TSortKey extends string
>(config: UrlParamConfig<TFilters, TSortKey>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Parse URL -> State
  const state = useMemo((): ListState<TFilters, TSortKey> => {
    const q = searchParams.get('q') || '';
    const statusParam = searchParams.get('status');
    const status: StatusFilter = 
      statusParam === 'active' || statusParam === 'inactive' 
        ? statusParam 
        : config.defaults.status;
    
    const sortKey = (searchParams.get('sort') || config.defaults.sortKey) as TSortKey;
    const dirParam = searchParams.get('dir');
    const sortDir: SortDirection = 
      dirParam === 'asc' || dirParam === 'desc' 
        ? dirParam 
        : config.defaults.sortDir;

    // Parse filters
    const filters = { ...config.defaults.filters } as TFilters;
    
    for (const key of Object.keys(config.defaults.filters) as (keyof TFilters)[]) {
      const urlValue = searchParams.get(String(key));
      
      if (urlValue === null) continue;
      
      if (config.multiSelectKeys.includes(key)) {
        // Multi-Select: comma-separated -> array
        (filters as Record<string, unknown>)[String(key)] = urlValue.split(',').filter(Boolean);
      } else if (config.triStateKeys.includes(key)) {
        // TriState: all/present/missing
        if (urlValue === 'present' || urlValue === 'missing') {
          (filters as Record<string, unknown>)[String(key)] = urlValue as TriStateFilter;
        }
      } else {
        // Simple value
        (filters as Record<string, unknown>)[String(key)] = urlValue;
      }
    }

    return { q, status, filters, sortKey, sortDir };
  }, [searchParams, config]);

  // Serialize State -> URL
  const updateUrl = useCallback((newState: Partial<ListState<TFilters, TSortKey>>) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const merged = { ...state, ...newState };
      
      // q
      if (merged.q) {
        params.set('q', merged.q);
      } else {
        params.delete('q');
      }
      
      // status
      if (merged.status !== config.defaults.status) {
        params.set('status', merged.status);
      } else {
        params.delete('status');
      }
      
      // sort
      if (merged.sortKey !== config.defaults.sortKey) {
        params.set('sort', merged.sortKey);
      } else {
        params.delete('sort');
      }
      
      // dir
      if (merged.sortDir !== config.defaults.sortDir) {
        params.set('dir', merged.sortDir);
      } else {
        params.delete('dir');
      }
      
      // filters
      const mergedFilters = { ...config.defaults.filters, ...merged.filters };
      for (const key of Object.keys(config.defaults.filters) as (keyof TFilters)[]) {
        const value = mergedFilters[key];
        const defaultValue = config.defaults.filters[key];
        const paramKey = String(key);
        
        if (config.multiSelectKeys.includes(key)) {
          const arr = value as string[] | undefined;
          const defaultArr = defaultValue as string[] | undefined;
          if (arr && arr.length > 0 && JSON.stringify(arr) !== JSON.stringify(defaultArr)) {
            params.set(paramKey, arr.join(','));
          } else {
            params.delete(paramKey);
          }
        } else if (config.triStateKeys.includes(key)) {
          const triState = value as TriStateFilter;
          if (triState && triState !== 'all') {
            params.set(paramKey, triState);
          } else {
            params.delete(paramKey);
          }
        } else {
          if (value !== undefined && value !== null && value !== defaultValue && value !== '') {
            params.set(paramKey, String(value));
          } else {
            params.delete(paramKey);
          }
        }
      }
      
      return params;
    }, { replace: true });
  }, [setSearchParams, state, config]);

  // Actions
  const setQ = useCallback((q: string) => {
    // Debounced
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      updateUrl({ q });
    }, DEBOUNCE_MS);
  }, [updateUrl]);

  const setQImmediate = useCallback((q: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    updateUrl({ q });
  }, [updateUrl]);

  const setStatus = useCallback((status: StatusFilter) => {
    updateUrl({ status });
  }, [updateUrl]);

  const setFilter = useCallback(<K extends keyof TFilters>(key: K, value: TFilters[K]) => {
    updateUrl({ filters: { ...state.filters, [key]: value } });
  }, [updateUrl, state.filters]);

  const toggleInMultiFilter = useCallback(<K extends keyof TFilters>(key: K, value: string) => {
    const current = (state.filters[key] as string[] | undefined) || [];
    const newValue = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    updateUrl({ filters: { ...state.filters, [key]: newValue as TFilters[K] } });
  }, [updateUrl, state.filters]);

  const setSort = useCallback((sortKey: TSortKey, sortDir?: SortDirection) => {
    // Toggle direction if same key
    const newDir = sortDir ?? (state.sortKey === sortKey && state.sortDir === 'asc' ? 'desc' : 'asc');
    updateUrl({ sortKey, sortDir: newDir });
  }, [updateUrl, state.sortKey, state.sortDir]);

  const reset = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    state,
    actions: {
      setQ,
      setQImmediate,
      setStatus,
      setFilter,
      toggleInMultiFilter,
      setSort,
      reset,
    },
  };
}
